import { api } from "./client";
import type { BuyerLeadStatus } from "./buyers";
import type { CommunicationChannel, CommunicationDirection } from "./communications";

/**
 * The buyer ACTIONS — the row and bulk verbs a specialist works their book
 * with, as distinct from the record edits in `buyers.ts`.
 *
 * <p>Routes live on `BuyerActionsController` (`api/buyers`), all gated
 * `Buyers.Update` at the door with per-row ownership (`BuyerOwnership
 * .RequireWrite`) inside — so a bulk call reports per item, and a row the
 * caller may not write lands as a `failed` item rather than failing the
 * whole request.</p>
 */

export interface BuyerFollowUpRequest {
  title: string;
  /** An instant. From an `<input type="date">`, always via `calendarDateInputToIso`. */
  dueDate: string;
}

export interface LogBuyerContactRequest {
  channel: CommunicationChannel;
  /** Null for a meeting, which is not directional. */
  direction?: CommunicationDirection | null;
  /** REQUIRED for Email — the server refuses an email without one. */
  subject?: string | null;
  /** One of the shared call outcomes. REQUIRED for Call, offered for Voicemail. */
  outcome?: string | null;
  /** REQUIRED for Meeting — the server refuses a meeting without one. */
  modality?: string | null;
  note: string;
  occurredAt?: string | null;
  /** Schedule the next touch in the same call. */
  followUp?: BuyerFollowUpRequest | null;
}

export interface BuyerContactResultDto {
  communicationLogEntryId: string;
  lastContactAtUtc: string;
  followUpTaskId?: string | null;
}

export interface BulkBuyerStatusRequest {
  ids: string[];
  status: BuyerLeadStatus;
  reason?: string | null;
}

export interface BulkBuyerEducationRequest {
  ids: string[];
}

export type BulkBuyerActionOutcome = "succeeded" | "failed" | "skipped";

export interface BulkBuyerActionItemDto {
  id: string;
  outcome: BulkBuyerActionOutcome;
  detail?: string | null;
}

export interface BulkBuyerActionResultDto {
  requested: number;
  succeeded: number;
  failed: number;
  items: BulkBuyerActionItemDto[];
}

export const buyerActionsApi = {
  /** Records a contact on the buyer's communications log and stamps
   *  `lastContactAtUtc` forward. Optionally schedules the next follow-up. */
  logContact: (id: string, body: LogBuyerContactRequest) =>
    api.post<BuyerContactResultDto>(`/buyers/${id}/contacts`, body),

  /** Schedules a follow-up as a WorkTask related to the buyer. Resolves the task id. */
  scheduleFollowUp: (id: string, body: BuyerFollowUpRequest) =>
    api.post<string>(`/buyers/${id}/follow-ups`, body),

  bulkSetStatus: (body: BulkBuyerStatusRequest) =>
    api.post<BulkBuyerActionResultDto>("/buyers/bulk-status", body),

  bulkSendEducationEmail: (body: BulkBuyerEducationRequest) =>
    api.post<BulkBuyerActionResultDto>("/buyers/bulk-education-email", body),
};
